/**
 * Query Utilities
 * ---------------
 * Helper functions for working with query clients generically
 * Abstracts adapter-specific differences (RTK Query, Fetch, TanStack Query, etc.)
 */

/**
 * Execute a query using the appropriate adapter pattern
 * Handles both RTK Query (dispatch thunk) and Fetch/TanStack (call unwrap directly)
 * 
 * @param initiateResult - Result from queryClient.endpoints[name].initiate()
 * @param dispatch - Redux dispatch function (only needed for RTK Query)
 * @returns Promise with the query result
 */
export async function executeQuery<T>(
  initiateResult: unknown,
  dispatch?: (action: unknown) => { unwrap: () => Promise<T> }
): Promise<T> {
  // RTK Query: initiate() returns a thunk function that needs to be dispatched
  if (typeof initiateResult === 'function') {
    if (!dispatch) {
      throw new Error('Dispatch is required for RTK Query adapter');
    }
    return await dispatch(initiateResult).unwrap();
  }
  
  // Fetch/TanStack Query: initiate() returns an object with unwrap() method
  if (
    initiateResult &&
    typeof initiateResult === 'object' &&
    'unwrap' in initiateResult &&
    typeof (initiateResult as { unwrap: () => Promise<T> }).unwrap === 'function'
  ) {
    return await (initiateResult as { unwrap: () => Promise<T> }).unwrap();
  }
  
  throw new Error('Unsupported query client adapter - initiate() must return a function (RTK Query) or object with unwrap() method (Fetch/TanStack)');
}

